import { useRef, useCallback, useEffect, useState } from "react";
import {
  BELL_MUTED_STORAGE_KEY,
  BELL_VOLUME_STORAGE_KEY,
} from "../constants";

/**
 * useBellTolls — generates a soft, procedurally-synthesised church bell sound
 * via the Web Audio API. No external assets.
 *
 * Public surface:
 *   toll(count)             - schedule N strikes spaced 1.4s apart
 *   unlock()                - resume the AudioContext (call from a user gesture)
 *   muted / setMuted(bool)  - state + setter, persisted to localStorage
 *   volume / setVolume(num) - state + setter (0..1), persisted to localStorage
 *   lastTollHourRef         - ref the bell controller uses to debounce hourly tolls
 */
export function useBellTolls() {
  const audioCtxRef = useRef(null);
  const isUnlockedRef = useRef(false);
  const lastTollHourRef = useRef(-1);

  const readPersisted = () => {
    if (typeof window === "undefined") return { muted: false, volume: 0.7 };
    let muted = false;
    let volume = 0.7;
    try {
      const m = window.localStorage.getItem(BELL_MUTED_STORAGE_KEY);
      if (m === "1") muted = true;
      const v = window.localStorage.getItem(BELL_VOLUME_STORAGE_KEY);
      if (v !== null) {
        const parsed = parseFloat(v);
        if (Number.isFinite(parsed)) volume = Math.max(0, Math.min(1, parsed));
      }
    } catch (e) {
      /* ignore */
    }
    return { muted, volume };
  };

  const initial = readPersisted();
  const [muted, setMutedState] = useState(initial.muted);
  const [volume, setVolumeState] = useState(initial.volume);
  const mutedRef = useRef(initial.muted);
  const volumeRef = useRef(initial.volume);

  const setMuted = useCallback((v) => {
    const next = !!v;
    mutedRef.current = next;
    setMutedState(next);
    try {
      window.localStorage.setItem(BELL_MUTED_STORAGE_KEY, next ? "1" : "0");
    } catch (e) {
      /* ignore */
    }
  }, []);

  const setVolume = useCallback((v) => {
    const next = Math.max(0, Math.min(1, Number(v) || 0));
    volumeRef.current = next;
    setVolumeState(next);
    try {
      window.localStorage.setItem(BELL_VOLUME_STORAGE_KEY, String(next));
    } catch (e) {
      /* ignore */
    }
  }, []);

  const ensureCtx = useCallback(() => {
    if (typeof window === "undefined") return null;
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      audioCtxRef.current = new Ctx();
    }
    return audioCtxRef.current;
  }, []);

  const unlock = useCallback(() => {
    const ctx = ensureCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }
    isUnlockedRef.current = true;
  }, [ensureCtx]);

  // One bell strike — fundamental + harmonics with exponential decay.
  const strike = useCallback(
    (when, baseVolume = 0.18) => {
      const ctx = ensureCtx();
      if (!ctx || mutedRef.current || volumeRef.current <= 0) return;
      const v = baseVolume * volumeRef.current;
      const fundamental = 520;
      const harmonics = [
        { ratio: 1.0, gain: 1.0, decay: 3.2 },
        { ratio: 2.0, gain: 0.45, decay: 2.4 },
        { ratio: 2.78, gain: 0.55, decay: 2.0 },
        { ratio: 4.2, gain: 0.25, decay: 1.4 },
        { ratio: 5.4, gain: 0.18, decay: 1.0 },
      ];
      const master = ctx.createGain();
      master.gain.setValueAtTime(v, when);
      master.connect(ctx.destination);

      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.setValueAtTime(2800, when);
      lp.Q.setValueAtTime(0.7, when);
      lp.connect(master);

      harmonics.forEach((h) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(fundamental * h.ratio, when);
        g.gain.setValueAtTime(0, when);
        g.gain.linearRampToValueAtTime(h.gain, when + 0.012);
        g.gain.exponentialRampToValueAtTime(0.0001, when + h.decay);
        osc.connect(g);
        g.connect(lp);
        osc.start(when);
        osc.stop(when + h.decay + 0.05);
      });
    },
    [ensureCtx]
  );

  const toll = useCallback(
    (count = 1) => {
      const ctx = ensureCtx();
      if (!ctx || mutedRef.current || volumeRef.current <= 0) return;
      if (!isUnlockedRef.current && ctx.state === "suspended") return;
      const n = Math.max(1, Math.min(12, count | 0));
      const now = ctx.currentTime + 0.05;
      for (let i = 0; i < n; i++) strike(now + i * 1.4, 0.18);
    },
    [ensureCtx, strike]
  );

  useEffect(
    () => () => {
      if (audioCtxRef.current) {
        try {
          audioCtxRef.current.close();
        } catch (e) {
          /* ignore */
        }
        audioCtxRef.current = null;
      }
    },
    []
  );

  return {
    toll,
    unlock,
    muted,
    setMuted,
    volume,
    setVolume,
    mutedRef,
    isUnlockedRef,
    lastTollHourRef,
  };
}
